
/**
 * Shared hook for the location save/unsave toggle.
 *
 * Owns: resolveLocationIdForSave → optimistic UI → saveLocation / unsaveLocation
 * → analytics → toast → rollback on error → busy guard → count resync.
 *
 * Consumers convert their domain type (TimelinePlace, PostPresentationActivity)
 * into PlaceCardData before calling `handleLocationSaveToggle`.
 */

import { useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  saveLocation,
  unsaveLocation,
  fetchLocationSaveCount,
} from '@/api/locationSaves';
import { resolveLocationIdForSave } from '@/utils/resolveLocationIdForSave';
import { isMongoObjectIdString } from '@/utils/idGeneration';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_LOCATION_SAVED,
  EVENT_LOCATION_UNSAVED,
  EVENT_LOCATION_SAVE_FAILED,
} from '@/lib/analyticsEvents';
import type { PlaceCardData } from '@/types/listViewTypes';
import type { UseLocationBookmarkStateReturn } from './useLocationBookmarkState';

type RequireAuth = (
  action: string,
  callback: () => void | Promise<void>,
) => void;

export interface UseLocationSaveToggleParams {
  user: { _id?: string | number } | null;
  requireAuth: RequireAuth;
  bookmarkState: UseLocationBookmarkStateReturn;
  /** Resolved once at the call site — e.g. `listPost._id`, `editingPostId`,
   *  or `post?._id ?? itineraryData.id`. */
  sourcePostId: string | undefined;
}

export interface UseLocationSaveToggleReturn {
  /**
   * Toggle bookmark for a place.
   *
   * Returns `'saved'`, `'unsaved'`, or `'error'` so the
   * `PlaceLocationSaveBookmarkButton` (split chevron / pill Continue) can open the
   * SaveToOverlay.
   */
  handleLocationSaveToggle: (
    placeCardData: PlaceCardData,
    sourcePostLocationId?: string,
    preResolvedLocationId?: string,
  ) => Promise<'saved' | 'unsaved' | 'error'>;
}

export function useLocationSaveToggle(
  params: UseLocationSaveToggleParams,
): UseLocationSaveToggleReturn {
  const { user, requireAuth, bookmarkState, sourcePostId } = params;
  const {
    userSavedLocationIds,
    setUserSavedLocationIds,
    setLocationSaveCountById,
    saveBusyIds,
    setSaveBusyIds,
  } = bookmarkState;

  const handleLocationSaveToggle = useCallback(
    async (
      placeCardData: PlaceCardData,
      sourcePostLocationId?: string,
      preResolvedLocationId?: string,
    ): Promise<'saved' | 'unsaved' | 'error'> => {
      if (!user) {
        requireAuth('save', () => {});
        return 'error';
      }

      let locId: string;
      let resolvedViaGoogle = false;

      if (preResolvedLocationId && isMongoObjectIdString(preResolvedLocationId)) {
        locId = preResolvedLocationId;
      } else {
        const resolved = await resolveLocationIdForSave({
          locationId: placeCardData.locationId,
          googlePlaceId: placeCardData.googlePlaceId ?? placeCardData.placeId ?? undefined,
          name: placeCardData.title,
          address: placeCardData.address,
        });

        if (!resolved) {
          WebAnalytics.trackContentInteraction(EVENT_LOCATION_SAVE_FAILED, {
            placeTitle: placeCardData.title,
            reason: 'location_id_unresolved',
          });
          toast.error("Couldn't save this place yet. Try again later.");
          return 'error';
        }

        locId = resolved.locationId;
        resolvedViaGoogle = resolved.resolvedViaGoogle;
      }

      const alreadySaved = userSavedLocationIds.has(locId);

      if (saveBusyIds.has(locId)) {
        return 'error';
      }

      setSaveBusyIds((prev) => new Set(prev).add(locId));

      // Optimistic UI: update state before the API call for instant feedback
      if (alreadySaved) {
        setUserSavedLocationIds((prev) => {
          const next = new Set(prev);
          next.delete(locId);
          return next;
        });
        setLocationSaveCountById((prev) => {
          const next = new Map(prev);
          const current = next.get(locId);
          if (typeof current === 'number') next.set(locId, Math.max(0, current - 1));
          return next;
        });
      } else {
        setUserSavedLocationIds((prev) => new Set(prev).add(locId));
        setLocationSaveCountById((prev) => {
          const next = new Map(prev);
          const current = next.get(locId);
          if (typeof current === 'number') next.set(locId, current + 1);
          return next;
        });
      }

      try {
        if (alreadySaved) {
          await unsaveLocation(locId);
          WebAnalytics.trackContentInteraction(EVENT_LOCATION_UNSAVED, {
            locationId: locId,
            placeTitle: placeCardData.title,
          });
          return 'unsaved';
        }

        await saveLocation({
          locationId: locId,
          sourcePostId,
          sourcePostLocationId,
        });
        WebAnalytics.trackContentInteraction(EVENT_LOCATION_SAVED, {
          locationId: locId,
          placeTitle: placeCardData.title,
          resolvedViaGoogle,
          sourcePostId: sourcePostId ?? null,
        });
        return 'saved';
      } catch {
        // Revert optimistic update on failure
        if (alreadySaved) {
          setUserSavedLocationIds((prev) => new Set(prev).add(locId));
          setLocationSaveCountById((prev) => {
            const next = new Map(prev);
            const current = next.get(locId);
            if (typeof current === 'number') next.set(locId, current + 1);
            return next;
          });
        } else {
          setUserSavedLocationIds((prev) => {
            const next = new Set(prev);
            next.delete(locId);
            return next;
          });
          setLocationSaveCountById((prev) => {
            const next = new Map(prev);
            const current = next.get(locId);
            if (typeof current === 'number') next.set(locId, Math.max(0, current - 1));
            return next;
          });
        }
        WebAnalytics.trackContentInteraction(EVENT_LOCATION_SAVE_FAILED, {
          placeTitle: placeCardData.title,
          reason: 'api_error',
        });
        toast.error(alreadySaved ? "Couldn't unsave this place." : "Couldn't save this place. Please try again.");
        return 'error';
      } finally {
        setSaveBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(locId);
          return next;
        });
        fetchLocationSaveCount(locId)
          .then((count) =>
            setLocationSaveCountById((prev) => {
              const next = new Map(prev);
              next.set(locId, count);
              return next;
            }),
          )
          .catch(() => {});
      }
    },
    [user, requireAuth, sourcePostId, userSavedLocationIds, saveBusyIds,
     setSaveBusyIds, setUserSavedLocationIds, setLocationSaveCountById],
  );

  return { handleLocationSaveToggle };
}
